import { useState } from "react";
import {
  Agreement03Icon,
  Checkbox,
  FormTable,
  FormTableBody,
  InformationCircleIcon,
  Input,
  Label,
} from "@porchpass/ui";
import type { FieldUpdaterType } from "src/hooks";
import { DisclaimerList } from "src/components/Disclaimer";

const formTableIconClassName = "text-[#6F41E6]";

export const TermsAgreement = ({
  clickHandler,
  checked,
  errorMessage,
  hasAttemptedFormSubmission,
}: {
  clickHandler: () => void;
  checked: boolean;
  errorMessage?: string;
  hasAttemptedFormSubmission?: boolean;
}) => {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const showError =
    Boolean(errorMessage) &&
    !checked &&
    (hasUserInteracted || hasAttemptedFormSubmission);

  return (
    <FormTable
      title="Terms"
      headerIcon={
        <Agreement03Icon
          variant="bulk-rounded"
          size={16}
          className={formTableIconClassName}
        />
      }
    >
      <FormTableBody className="space-y-4 text-sm leading-5 text-[#4a5466]">
        <p>
          I understand and agree that I am providing instructions to PorchPass,
          LLC under the Fair Credit Reporting Act (“FCRA”) to access information
          from my personal credit profile or other information from one or more
          consumer reporting agencies such as Equifax, Experian, or TransUnion.
          I authorize PorchPass, LLC to verify my identity, display my credit
          information to me, and assess my pre-qualification for a mobile home
          loan.
        </p>
        <p>
          By checking the box and proceeding, we will initiate a credit inquiry
          that may affect your credit score.
        </p>
        <div
          className="flex flex-col gap-1.5"
          data-form-field-error={showError ? "true" : undefined}
        >
          <div className="flex items-center gap-4 rounded-lg bg-surface-primary-disabled px-[15px] py-3">
            <Checkbox
              id="terms_acknowledgement"
              checked={checked}
              aria-invalid={showError ? true : undefined}
              onCheckedChange={() => {
                setHasUserInteracted(true);
                clickHandler();
              }}
            />
            <Label
              htmlFor="terms_acknowledgement"
              className="text-base font-medium leading-5 text-[#4a5466]"
            >
              I understand the terms above
            </Label>
          </div>
          {showError && (
            <p className="ml-6 text-3xs text-[#872525]">{errorMessage}</p>
          )}
        </div>
      </FormTableBody>
    </FormTable>
  );
};

export const DisclaimerWithAcknowledgement = ({
  changeHandler,
  errorMessage,
  hasAttemptedFormSubmission,
  signature,
}: {
  changeHandler: FieldUpdaterType;
  errorMessage?: string;
  hasAttemptedFormSubmission?: boolean;
  signature?: string;
}) => (
  <FormTable
    title="Disclaimer"
    headerIcon={
      <InformationCircleIcon
        variant="bulk-rounded"
        size={16}
        className={formTableIconClassName}
      />
    }
  >
    <FormTableBody className="space-y-4 text-sm leading-5 text-[#4a5466]">
      <p>
        By typing my name below, I understand and agree to the following terms
        and conditions
      </p>
      <DisclaimerList>
        <li>
          I provide my express written consent to receive calls and text
          messages, including for marketing purposes, from Porchpass, Porchpass’
          Network Partners and any authorized third party calling or texting on
          behalf of Porchpass or its Network Partners, including calls and texts
          made through automated means such as autodialers, selection systems,
          and prerecorded or artificial voice recordings, even if my number is
          listed on any company-specific, state, or federal Do-Not-Call list.
          Message and data rates may apply.
        </li>
        <li>
          I authorize Porchpass and its Network Partners with whom I am matched
          to obtain my consumer report, credit profile, or other credit
          information associated with me from any consumer reporting agency in
          connection with my inquiry.
        </li>
        <li>
          I authorize Porchpass to share my information with its Network
          Partners, and they may further share my information with their
          partners. Porchpass and its Network Partners may exchange information
          about me, including my loan terms and account.
        </li>
        <li>
          I agree to the Mortgage Broker Disclosures and Broker Agreement for my
          state.
        </li>
        <li>
          I agree to Porchpass’ Terms of Use Agreement, Privacy Policy, and to
          receive communications electronically.
        </li>
      </DisclaimerList>
      <Input
        label="Full Name"
        name="full_name_signature"
        value={signature ?? ""}
        onChange={(event) => changeHandler(event.target.value)}
        error={Boolean(errorMessage && hasAttemptedFormSubmission)}
        helperText={hasAttemptedFormSubmission ? errorMessage : undefined}
      />
    </FormTableBody>
  </FormTable>
);
